import React, { useState, useEffect, useCallback } from "react";
import { Box, Grid2, Stack, TextField, Typography, IconButton, Button as MuiButton, Drawer } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import { getTokenbyData, getUser } from "../../services/user.service";
import Autocomplete from "@mui/material/Autocomplete";
import { getAllProject, getReports } from "../../services/reports.service";
import DailyReportTable from "./Components/DailyReportTable";
import IssueTypeDrillDownTable from "./Components/IssueTypeDrillDownTable";
import { Button, DatePicker } from "antd";
import { ToastError } from "../../utils/Toaster/Toaster";
import { getDailyWorkingReport } from "../../services/reports.service";
import dayjs from "dayjs";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../context/AuthProvider";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ROLES } from "../../utils/roles";
const { RangePicker } = DatePicker;

const DailyWorkingReport = () => {
  // Set default date to today
  const getDefaultDate = () => {
    return dayjs();
  };

  const [selectedDate, setSelectedDate] = useState(getDefaultDate());
  const [data, setData] = useState([]);
  const [employeeData, setEmployeeData] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedEmployeeId, setselectedEmployeeId] = useState(null);
  const [projectDatas, setProjectDatas] = useState([]);
  const [totalHours, setTotalHours] = useState("");
  const [isOverAllAdmin, setIsOverAllAdmin] = useState(false);
  const navigate = useNavigate();
  const [isManager, setisManager] = useState(false);
  const [drillDownView, setDrillDownView] = useState(null); // { projectName, issueType } or null
  const [showFilters, setShowFilters] = useState(false);

  const fetchTasks = async () => {
    try {
      const res = await getTokenbyData();
      const { isOverAllAdmin, isManager, role } = res.data || {};
      // An Org Admin is not isOverAllAdmin, but it still drives this page in
      // "pick an employee" mode — the backend narrows the list to its own org.
      setIsOverAllAdmin(isOverAllAdmin || role === ROLES.ORG_ADMIN);
      setisManager(isManager);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const { accessToken } = useAuth();
  const getUserIdFromToken = () => {
    if (!accessToken) return null;
    try {
      const decoded = jwtDecode(accessToken);
      return decoded.id;
    } catch (error) {
      console.error("Invalid token", error);
      return null;
    }
  };

  const userId = getUserIdFromToken();



  const fetchReportData = useCallback(async () => {
    const dateToUse = !selectedDate ? getDefaultDate() : selectedDate;
    const reportUserId = isOverAllAdmin ? selectedEmployeeId : userId;

    if (!reportUserId) {
      return;
    }

    try {
      const formattedDate = dayjs(dateToUse).format("YYYY-MM-DD");

      const res = await getDailyWorkingReport({
        date: formattedDate,
        userId: reportUserId,
        // projectId: selectedProjectId,
      });

      if (res?.data) {
        setData(res.data);

        // Calculate total hours from the processed data
        const totalMinutes = res.data.reduce((sum, item) => {
          if (item.totalHours) {
            const [hours, minutes] = item.totalHours.split(":").map(Number);
            return sum + hours * 60 + minutes;
          }
          return sum;
        }, 0);

        const totalH = Math.floor(totalMinutes / 60);
        const totalM = totalMinutes % 60;
        const pad = (num) => num.toString().padStart(2, "0");

        setTotalHours(`${pad(totalH)}:${pad(totalM)}`);
      }
    } catch (error) {
      console.log("Error in fetching report", error);
    }
  }, [
    selectedDate,
    selectedEmployeeId,
    isOverAllAdmin,
    userId,
  ]);

  const handleUserChange = (event, value) => {
    if (value?.label === "All") {
      setSelectedEmployee("All");
      setselectedEmployeeId("All");
    } else {
      setSelectedEmployee(value?.name || value?.label);
      setselectedEmployeeId(value?._id);
    }
  };

  const handleProjectChange = (event, newValue) => {
    if (newValue === "All") {
      setSelectedProject("All");
      setSelectedProjectId("All");
    } else {
      const project = projectDatas.find((pro) => pro.title === newValue);
      setSelectedProject(project?.title);
      setSelectedProjectId(project?._id);
    }
  };

  const handleDateChange = (date) => {
    if (date) {
      // Valid date selected
      setSelectedDate(date);
    } else {
      // Date cleared, reset to today
      setSelectedDate(getDefaultDate());
    }
  };

  const handleRowClick = (projectName, issueType) => {
    setDrillDownView({ projectName, issueType });
  };

  const handleBackToSummary = () => {
    setDrillDownView(null);
  };

  // Filter data for drill down view
  const getDrillDownData = () => {
    if (!drillDownView) return [];
    // If issueType is null, show all tasks for the project (Total Hours clicked)
    if (drillDownView.issueType === null) {
      return data.filter((item) => item.projectName === drillDownView.projectName);
    }
    // Otherwise, filter by both project and issue type
    return data.filter(
      (item) =>
        item.projectName === drillDownView.projectName &&
        item.issueType === drillDownView.issueType
    );
  };

  // Recalculate total hours when drill-down view changes
  useEffect(() => {
    if (drillDownView) {
      const drillDownData = getDrillDownData();
      const totalMinutes = drillDownData.reduce((sum, item) => {
        if (item.totalHours) {
          const [hours, minutes] = item.totalHours.split(":").map(Number);
          return sum + hours * 60 + minutes;
        }
        return sum;
      }, 0);

      const totalH = Math.floor(totalMinutes / 60);
      const totalM = totalMinutes % 60;
      const pad = (num) => num.toString().padStart(2, "0");

      setTotalHours(`${pad(totalH)}:${pad(totalM)}`);
    } else {
      // Recalculate from all data when back to summary view
      const totalMinutes = data.reduce((sum, item) => {
        if (item.totalHours) {
          const [hours, minutes] = item.totalHours.split(":").map(Number);
          return sum + hours * 60 + minutes;
        }
        return sum;
      }, 0);

      const totalH = Math.floor(totalMinutes / 60);
      const totalM = totalMinutes % 60;
      const pad = (num) => num.toString().padStart(2, "0");

      setTotalHours(`${pad(totalH)}:${pad(totalM)}`);
    }
  }, [drillDownView, data]);

  // Fetch data when filters change
  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  // Initialize with default values when data loads
  useEffect(() => {
    if (employeeData.length > 0 && !selectedEmployee) {
      setSelectedEmployee("All");
      setselectedEmployeeId("All");
    }
  }, [employeeData, selectedEmployee]);

  useEffect(() => {
    if (projectDatas.length > 0 && !selectedProject) {
      setSelectedProject("All");
      setSelectedProjectId("All");
    }
  }, [projectDatas, selectedProject]);

  useEffect(() => {
    const fetchEmployeeData = async () => {
      // Only the employee-picker mode needs the list; an org employee is not
      // allowed to call /getUser at all.
      if (!isOverAllAdmin) return;
      try {
        const res = await getUser({ isEnabled: true });
        const configData = res.data.data.map((e) => ({
          ...e,
          label: e?.firstName + (e?.lastName ? " " + e.lastName : ""),
          name: e?.firstName + (e?.lastName ? " " + e.lastName : ""),
        }));
        setEmployeeData(configData);
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };

    fetchEmployeeData();
  }, []);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const res = await getAllProject();
        if (res?.data) {
          setProjectDatas(res?.data);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };

    fetchProject();
  }, []);

  const handleView = (task) => {
    navigate(`/home/taskDetails?taskId=${task._id}`, {
      state: {
        isOverAllAdmin: isOverAllAdmin,
        isManager: isManager
      },
    });
  };

  return (
    <div className="app-container">
      <Box sx={{ width: "100%", overflowX: "hidden", mt: { xs: -2, md: 0 } }}>
        {/* Mobile: Filter Button + Total Hours Row */}
        <Box sx={{ display: { xs: "flex", md: "none" }, justifyContent: "space-between", gap: 1.5, mb: 2 }}>
          <MuiButton
            variant={showFilters ? "contained" : "outlined"}
            onClick={() => setShowFilters(!showFilters)}
            sx={{
              minWidth: "48px",
              width: "48px",
              height: "48px",
              padding: 0,
              borderColor: "#E5E7EB",
              borderRadius: "12px",
              color: showFilters ? "#fff" : "#6B7280",
              backgroundColor: showFilters ? "#008080" : "#FFFFFF",
              "&:hover": {
                borderColor: "#D1D5DB",
                backgroundColor: showFilters ? "#006666" : "#F9FAFB",
              }
            }}
          >
            {showFilters ? <CloseIcon /> : <FilterListIcon />}
          </MuiButton>

          {/* Total Hours (Mobile) */}
          {totalHours && (
            <Box
              sx={{
                bgcolor: "#008080",
                color: "white",
                padding: "8px 16px",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0, 128, 128, 0.2)",
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
                justifyContent: "center"
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "13px" }}>
                Total Hours:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "16px" }}>
                {totalHours}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Filter Drawer (Mobile) */}
        <Drawer
          anchor="bottom"
          open={showFilters}
          onClose={() => setShowFilters(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: "20px",
              borderTopRightRadius: "20px",
              padding: "20px",
              maxHeight: "85vh"
            }
          }}
          sx={{ display: { xs: "block", md: "none" } }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1F2937" }}>
              Filters
            </Typography>
            <IconButton onClick={() => setShowFilters(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Stack spacing={3}>
            {/* Date Picker */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "#374151", fontWeight: 500 }}>
                Date
              </Typography>
              <DatePicker
                value={selectedDate}
                onChange={handleDateChange}
                className="datePicker"
                format={"DD-MM-YYYY"}
                allowClear={true}
                placeholder="Select Date"
                style={{ width: "100%" }}
                placement="topLeft"
                popupStyle={{ zIndex: 1301 }}
              />
            </Box>

            {/* Employee Filter (Admin Only) */}
            {isOverAllAdmin && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: "#374151", fontWeight: 500 }}>
                  Employee
                </Typography>
                <Autocomplete
                  value={selectedEmployee ? { label: selectedEmployee } : null}
                  options={[...employeeData]}
                  getOptionLabel={(option) => option.label || option}
                  onChange={handleUserChange}
                  placeholder="Select Employee"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Employee"
                      size="small"
                      sx={{
                        bgcolor: "white",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                        }
                      }}
                    />
                  )}
                  sx={{ width: "100%" }}
                />
              </Box>
            )}
          </Stack>
        </Drawer>

        {/* Desktop Filters & Total Hours (Original Layout) */}
        <Box sx={{ display: { xs: "none", md: "block" }, mb: 2 }}>
          <Grid2 container spacing={2} alignItems="center">
            {/* Date Picker */}
            <Grid2 size={{ xs: 12, md: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                {drillDownView && (
                  <IconButton
                    onClick={handleBackToSummary}
                    sx={{
                      bgcolor: "#008080",
                      color: "white",
                      width: 40,
                      height: 40,
                      "&:hover": {
                        bgcolor: "#006666",
                      },
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                )}
                <DatePicker
                  value={selectedDate}
                  onChange={handleDateChange}
                  className="datePicker"
                  format={"DD-MM-YYYY"}
                  allowClear={true}
                  placeholder="Select Date"
                  style={{ width: "100%" }}
                />
              </Stack>
            </Grid2>

            {/* Employee Filter (Admin Only) */}
            {isOverAllAdmin && (
              <Grid2 size={{ xs: 12, md: 3 }}>
                <Autocomplete
                  value={selectedEmployee ? { label: selectedEmployee } : null}
                  options={[...employeeData]}
                  getOptionLabel={(option) => option.label || option}
                  onChange={handleUserChange}
                  placeholder="Select Employee"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Employee"
                      size="small"
                      sx={{
                        bgcolor: "white",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                        }
                      }}
                    />
                  )}
                  sx={{ width: "100%" }}
                />
              </Grid2>
            )}

            {/* Total Hours Box (Right Aligned) */}
            <Grid2 size={{ xs: 12, md: isOverAllAdmin ? 6 : 9 }} display="flex" justifyContent="flex-end">
              {totalHours && (
                <Box
                  sx={{
                    bgcolor: "#008080",
                    color: "white",
                    padding: "10px 20px",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0, 128, 128, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "14px" }}>
                    Total Hours:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "18px" }}>
                    {totalHours}
                  </Typography>
                </Box>
              )}
            </Grid2>
          </Grid2>
        </Box>
        {/* <Grid2 size={{ xs: 4 }}>
            <Typography variant="subtitle2" gutterBottom>
              Project
            </Typography>
            <Autocomplete
              value={selectedProject || null}
              onChange={handleProjectChange}
              options={["All", ...projectDatas.map((item) => item.title)]}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Project"
                  className="AutoComplete_InputBox"
                  size="small"
                />
              )}
              sx={{ width: "100%" }}
            />
          </Grid2> */}

        {drillDownView ? (
          <IssueTypeDrillDownTable
            data={getDrillDownData()}
            handleView={handleView}
          />
        ) : (
          <DailyReportTable data={data} onRowClick={handleRowClick} />
        )}
      </Box>
    </div >
  );
};

export default DailyWorkingReport;